import type { PerspectiveCamera } from 'three';

/**
 * The logical play field, and the camera framing that keeps it constant.
 *
 * This is the resolution-independence fix identified during the Unity survey:
 * that build clamped the player to the *camera viewport*, so an ultrawide monitor
 * granted a larger play area than a phone and the game changed with the window.
 *
 * Here the field is a fixed circle in world units and the camera is fitted to it,
 * so every display sees the same playable region. Aspect ratios wider than the
 * field simply reveal more surrounding space, which is scenery, never play area.
 *
 * The prototype uses a perspective camera because the world it has to sell is a
 * depth-filled one — stars, parallax rings, a camera that breathes. The fit below
 * is what makes that safe: gameplay framing is a computed distance, not a
 * consequence of the window.
 */
export class PlayField {
  /** Radius of the playable circle, in world units. */
  readonly radius: number;
  /** Field-of-view height the camera must cover: the field plus its margin. */
  private readonly framedRadius: number;

  constructor(radius = 9, margin = 1.18) {
    this.radius = radius;
    this.framedRadius = radius * margin;
  }

  /**
   * Distance at which `camera` frames the whole field.
   *
   * Fits by whichever axis is tighter, so the field is always fully visible
   * ("contain", never "cover"). Portrait windows pull the camera back rather than
   * cropping the mandala.
   */
  fitDistance(camera: PerspectiveCamera): number {
    const vertical = (camera.fov * Math.PI) / 180;
    const distanceForHeight = this.framedRadius / Math.tan(vertical / 2);
    const horizontal = 2 * Math.atan(Math.tan(vertical / 2) * camera.aspect);
    const distanceForWidth = this.framedRadius / Math.tan(horizontal / 2);
    return Math.max(distanceForHeight, distanceForWidth);
  }
}
